import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.addEventListener('change', this.#handleSizeChange);
    this.addEventListener('toggle', this.#handleDisclosureToggle, true);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.removeEventListener('change', this.#handleSizeChange);
    this.removeEventListener('toggle', this.#handleDisclosureToggle, true);
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #handleDisclosureToggle = (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      requestAnimationFrame(() => this.#updateStickyState());
    }
  };

  #handleSizeChange = async (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-tn-cart-size-select]')) return;

    const oldVariantId = Number(select.dataset.currentVariant);
    const newVariantId = Number(select.value);
    const oldKey = select.dataset.key;
    if (!oldKey || !newVariantId || newVariantId === oldVariantId) return;

    const errorElement = select.closest('.cart-items__variants-wrapper')?.querySelector('.tn-cart-size-error');
    const root = window.Shopify?.routes?.root || '/';
    let cartChanged = false;
    select.disabled = true;
    const setCheckoutDisabled = (disabled) => {
      document.querySelectorAll('#cart-drawer [name="checkout"]').forEach((button) => { button.disabled = disabled; });
    };
    setCheckoutDisabled(true);
    this.setAttribute('aria-busy', 'true');

    try {
      let cart;
      let oldLine;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const cartResponse = await fetch(`${root}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error('Panier indisponible. Réessayez.');
        cart = await cartResponse.json();
        oldLine = cart.items.find((item) => item.key === oldKey);
        if (oldLine) break;
        await new Promise((resolve) => window.setTimeout(resolve, 200 * (attempt + 1)));
      }
      if (!oldLine) throw new Error('Le panier a changé. Rouvrez-le pour choisir la taille.');

      const matchingLines = cart.items.filter((item) => item.variant_id === newVariantId);
      if (matchingLines.length > 1 || matchingLines.some((item) => Object.keys(item.properties || {}).length || item.selling_plan_allocation)) {
        throw new Error('Cette taille est déjà dans votre panier. Vérifiez les articles avant de continuer.');
      }

      const targetLine = matchingLines[0];
      const updates = {
        [oldKey]: 0,
        [targetLine?.key || newVariantId]: oldLine.quantity + (targetLine?.quantity || 0),
      };
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections: 'cart-drawer-section', sections_url: '/' }),
      });
      const result = await response.json();
      if (!response.ok || result.errors) throw new Error(result.description || 'Impossible de modifier la taille.');
      cartChanged = true;

      const html = result.sections?.['cart-drawer-section'];
      if (!html) throw new Error('Le panier ne peut pas se mettre à jour.');
      sectionRenderer.abortRender('cart-drawer-section');
      await morphSection('cart-drawer-section', html, { mode: 'hydration' });

      const trigger = document.querySelector('[data-tn-header] [aria-controls="cart-drawer"]');
      if (trigger) {
        trigger.setAttribute('aria-label', `Panier (${result.item_count})`);
        let badge = trigger.querySelector('.tn-count');
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'tn-count';
          trigger.appendChild(badge);
        }
        badge.textContent = String(result.item_count);
      }

      document.querySelector(`#cart-drawer [data-tn-cart-size-select][data-current-variant="${newVariantId}"]`)?.focus({ preventScroll: true });
    } catch (error) {
      if (cartChanged) {
        window.location.assign(root + 'cart');
        return;
      }
      select.value = String(oldVariantId);
      if (errorElement) {
        errorElement.textContent = error?.message || 'Impossible de modifier la taille.';
        errorElement.hidden = false;
      }
    } finally {
      select.disabled = false;
      setCheckoutDisabled(false);
      document.querySelector('#cart-drawer cart-drawer-component')?.removeAttribute('aria-busy');
    }
  };

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
